import { DependencyList, useEffect, useRef } from 'react';
import { Observable, Subject, switchAll } from 'rxjs';
import { useCreation } from '../hooks/useCreation';
import { useThrowErrorInReactLifecycle } from '../reactExtensions';
import { useRxScheduleRerender } from './useRxScheduleRerender';

export function useLocalObservable<T>(
  source: () => Observable<T>,
  deps: DependencyList,
): undefined | T;
export function useLocalObservable<T>(
  source: () => Observable<T>,
  deps: DependencyList,
  startWithValue: T,
): T;
export function useLocalObservable<T>(
  source: () => Observable<T>,
  deps: DependencyList,
  startWithValue?: T,
): undefined | T {
  const latestValueRef = useRef<undefined | T>(startWithValue);

  const scheduleRerender = useRxScheduleRerender();

  const throwErrorInReactLifecycle = useThrowErrorInReactLifecycle();

  const source$ = useCreation(() => new Subject<Observable<T>>(), []);

  useEffect(() => {
    const subscription = source$.pipe(switchAll()).subscribe({
      next: v => {
        latestValueRef.current = v;
        scheduleRerender();
      },
      error: throwErrorInReactLifecycle,
    });
    return () => subscription.unsubscribe();
  }, [scheduleRerender, source$, throwErrorInReactLifecycle]);

  useEffect(
    () => {
      source$.next(source());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps,
  );

  return latestValueRef.current;
}
